import { http, publicHttp } from 'core/utils/httpClient';
import type { CreateOrderFileRequest } from './dtoModels/order/createOrderFileRequest';
import type { OrderFileResponse } from './dtoModels/order/orderFileResponse';
import type { UploadOrderFileResponse } from './dtoModels/order/uploadOrderFileResponse';
import type { AxiosProgressEvent, AxiosResponse } from 'axios';
import type { OrderFile } from 'models/order/orderFile';

export const mapOrderFile = (source: OrderFileResponse): OrderFile => ({
  ...source,
  date: new Date(source.dateCreate),
});

export const mapOrderFiles = (source: OrderFileResponse[]): OrderFile[] =>
  source.map((s) => mapOrderFile(s));

export async function getOrderFilesAsync(orderId: number): Promise<OrderFile[]> {
  const { data } = await http.get<OrderFileResponse[]>(`orders/${orderId}/files`);

  return mapOrderFiles(data);
}

export async function uploadOrderFileAsync(
  orderId: number,
  file: File,
  options?: {
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
    signal?: AbortSignal;
  }
): Promise<string> {
  const { data } = await http.post<
    UploadOrderFileResponse,
    AxiosResponse<UploadOrderFileResponse>,
    CreateOrderFileRequest
  >(`orders/${orderId}/files`, {
    name: file.name,
    fileSize: file.size,
  });

  try {
    await publicHttp.put(data.uploadUrl, file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      ...options,
    });
  } catch {
    await deleteOrderFilesAsync(orderId, [data.id]);
  }

  return data.name;
}

export async function deleteOrderFilesAsync(
  orderId: number,
  orderFileIds: number[]
): Promise<void> {
  await http.delete(`orders/${orderId}/files`, { data: orderFileIds });
}

export async function getDownloadFileAsync(
  file: OrderFile,
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void
): Promise<Blob> {
  const { data } = await publicHttp.get<Blob>(file.url, {
    responseType: 'blob',
    onDownloadProgress,
  });

  return data;
}
